import { useMemo, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Sparkles } from "lucide-react";

/**
 * DailyConstellation — a quiet piece of living art. Each ritual logged this
 * week becomes a glowing star on an SVG night sky. Stars from the same day
 * are connected with soft lines, so a completed day looks like a mini
 * constellation. Today's stars pulse; past days are steady.
 *
 * Input: `ritualsByDay` from /api/weekly-summary.week_rituals_by_day
 *   { Mon: { journal: true, meal: true }, Tue: {}, ... Sun: {} }
 */
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Fixed Y-position per ritual type so a given ritual always shows at the
// same "altitude" — creating visual rhythm as you scan across days.
const RITUAL_Y = {
  vibe_check: 18,
  meditation: 32,
  journal: 46,
  affirmation: 60,
  meal: 74,
  exercise: 88,
  symptom: 102,
};

const RITUAL_COLORS = {
  vibe_check: "#f472b6",   // pink
  meditation: "#a78bfa",   // violet
  journal: "#60a5fa",      // blue
  affirmation: "#facc15",  // amber
  meal: "#34d399",         // emerald
  exercise: "#fb923c",     // orange
  symptom: "#22d3ee",      // cyan
};

// Pretty labels for the legend (reads left-to-right)
const RITUAL_LABELS = {
  vibe_check: "Vibe Check",
  meditation: "Meditation",
  journal: "Journal",
  affirmation: "Affirmation",
  meal: "Meal",
  exercise: "Movement",
  symptom: "Symptoms",
};

// Order shown in legend (top → bottom of sky)
const RITUAL_ORDER = [
  "vibe_check",
  "meditation",
  "journal",
  "affirmation",
  "meal",
  "exercise",
  "symptom",
];

const DailyConstellation = ({ ritualsByDay }) => {
  // null = show all. Otherwise filters the sky to one ritual type.
  const [activeFilter, setActiveFilter] = useState(null);

  // Compute current weekday index (0 = Mon) in local time
  const todayIdx = useMemo(() => {
    const js = new Date().getDay(); // 0 = Sun
    return js === 0 ? 6 : js - 1;
  }, []);

  // Build stars: { day, ritual, x, y }
  const stars = useMemo(() => {
    if (!ritualsByDay) return [];
    const arr = [];
    DAYS.forEach((day, i) => {
      const entry = ritualsByDay[day] || {};
      // Evenly space the 7 columns from x=10% to x=90%
      const x = 10 + (i * 80) / 6;
      Object.keys(entry).forEach((ritual) => {
        if (entry[ritual] && RITUAL_Y[ritual] != null) {
          arr.push({
            day,
            dayIdx: i,
            ritual,
            x,
            y: RITUAL_Y[ritual],
            color: RITUAL_COLORS[ritual] || "#cbd5e1",
            isToday: i === todayIdx,
          });
        }
      });
    });
    return arr;
  }, [ritualsByDay, todayIdx]);

  // Group stars per day for constellation-connecting lines
  const byDay = useMemo(() => {
    const grouped = {};
    stars.forEach((s) => {
      (grouped[s.dayIdx] = grouped[s.dayIdx] || []).push(s);
    });
    // Sort each day's stars top-to-bottom so the lines don't cross
    Object.values(grouped).forEach((arr) => arr.sort((a, b) => a.y - b.y));
    return grouped;
  }, [stars]);

  const completedDays = Object.keys(byDay).length;

  return (
    <Card
      className="bg-gradient-to-br from-slate-900/95 via-indigo-950/90 to-slate-900/95 border-indigo-500/20 overflow-hidden animate-fade-in"
      data-testid="daily-constellation-card"
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-300" />
            <p className="text-[11px] uppercase tracking-[0.15em] text-amber-200/80 font-semibold">
              Your Week in Stars
            </p>
          </div>
          <span className="text-[11px] text-indigo-200/70" data-testid="constellation-completed-days">
            {completedDays} {completedDays === 1 ? "day" : "days"} lit
          </span>
        </div>

        {/* Subtitle — tells users exactly what they're looking at */}
        <p className="text-[11px] text-indigo-200/60 mb-3 leading-snug">
          Each colored star is a ritual you logged. Tap a color below to focus
          your sky on just that ritual.
        </p>

        {/* Night-sky SVG */}
        <div className="relative" data-testid="constellation-svg-wrap">
          <svg
            viewBox="0 0 100 120"
            preserveAspectRatio="none"
            className="w-full h-40"
            role="img"
            aria-label={`Weekly ritual constellation — ${stars.length} rituals across ${completedDays} days`}
          >
            {/* Background twinkle dots (decorative, static) */}
            {[[15,12],[82,22],[38,108],[68,96],[92,58],[8,70]].map(([cx, cy], i) => (
              <circle key={`t-${i}`} cx={cx} cy={cy} r="0.4" fill="rgba(255,255,255,0.35)" />
            ))}

            {/* Constellation-connecting lines per day */}
            {Object.values(byDay).map((dayStars, idx) =>
              dayStars.slice(0, -1).map((star, i) => {
                const next = dayStars[i + 1];
                return (
                  <line
                    key={`line-${idx}-${i}`}
                    x1={star.x}
                    y1={star.y}
                    x2={next.x}
                    y2={next.y}
                    stroke="rgba(167,139,250,0.35)"
                    strokeWidth="0.3"
                    strokeLinecap="round"
                  />
                );
              })
            )}

            {/* Stars */}
            {stars.map((s, i) => {
              const dim = activeFilter && s.ritual !== activeFilter;
              return (
                <g
                  key={i}
                  data-testid={`constellation-star-${s.day}-${s.ritual}`}
                  style={{
                    opacity: dim ? 0.18 : 1,
                    transition: "opacity 0.4s ease",
                  }}
                >
                  {/* Outer glow */}
                  <circle
                    cx={s.x}
                    cy={s.y}
                    r={s.isToday ? 3.5 : 2.2}
                    fill={s.color}
                    opacity="0.25"
                  >
                    {s.isToday && !dim && (
                      <animate
                        attributeName="r"
                        values="2.8;4;2.8"
                        dur="2.6s"
                        repeatCount="indefinite"
                      />
                    )}
                  </circle>
                  {/* Core */}
                  <circle
                    cx={s.x}
                    cy={s.y}
                    r={s.isToday ? 1.3 : 1}
                    fill={s.color}
                  />
                </g>
              );
            })}
          </svg>

          {/* Day labels under the sky */}
          <div className="flex justify-between mt-1 px-1">
            {DAYS.map((d, i) => (
              <span
                key={d}
                className={`text-[10px] ${
                  i === todayIdx
                    ? "text-amber-300 font-semibold"
                    : byDay[i]
                    ? "text-indigo-200"
                    : "text-indigo-300/40"
                }`}
              >
                {d}
              </span>
            ))}
          </div>
        </div>

        {/* Color legend — tap to filter the sky to one ritual */}
        <div
          className="mt-4 pt-3 border-t border-indigo-500/15 flex flex-wrap gap-x-2 gap-y-1.5 items-center"
          data-testid="constellation-legend"
        >
          {RITUAL_ORDER.map((key) => {
            const isActive = activeFilter === key;
            const color = RITUAL_COLORS[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveFilter(isActive ? null : key)}
                data-testid={`constellation-legend-${key}`}
                className={`group flex items-center gap-1.5 px-2 py-1 rounded-full transition-all duration-200 ${
                  isActive
                    ? "bg-white/10 ring-1"
                    : "hover:bg-white/5"
                }`}
                style={isActive ? { boxShadow: `inset 0 0 0 1px ${color}99` } : {}}
                aria-pressed={isActive}
                aria-label={`Filter to ${RITUAL_LABELS[key]}`}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full transition-transform group-hover:scale-110"
                  style={{
                    backgroundColor: color,
                    boxShadow: `0 0 6px ${color}80`,
                  }}
                />
                <span
                  className={`text-[10px] transition-colors ${
                    isActive ? "text-white font-medium" : "text-indigo-200/75"
                  }`}
                >
                  {RITUAL_LABELS[key]}
                </span>
              </button>
            );
          })}
          {activeFilter && (
            <button
              type="button"
              onClick={() => setActiveFilter(null)}
              data-testid="constellation-legend-clear"
              className="ml-auto text-[10px] text-indigo-300/70 hover:text-indigo-100 underline underline-offset-2"
              aria-label="Show all rituals"
            >
              Show all
            </button>
          )}
        </div>

        {/* Empty state */}
        {stars.length === 0 && (
          <p className="mt-4 text-center text-xs text-indigo-200/70 italic">
            A new sky, waiting to be lit. Log one ritual to place your first star ✨
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default DailyConstellation;
